import React from 'react';
import { Table, Button } from 'react-bootstrap';
import { SalesReportResponseV4, SalesReportDataV4 } from '../../../../services/cm-report/response/SalesReportResponseV4';
import { formatPrice } from '../../../../shared/utils/formatPrice';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';
import { IconArrowDown, IconArrowUp } from '@tabler/icons-react';

type SortDirection = "asc" | "desc";
type OrderByOption = "property_name" | "booking_date" | "source" | "reservation_number" | "guest_name" | "checkin_date" | "checkout_date" | "extra" | "amount";

interface SalesReportTableV4Props {
    data: SalesReportResponseV4;
    sortConfig: { orderBy: string; sort: SortDirection };
    onSort: (orderBy: string) => void;
}

const SalesReportTableV4: React.FC<SalesReportTableV4Props> = ({ data, sortConfig, onSort }) => {
    const columnsSales = [
        { label: "Property", key: "property_name", sortKey: "property_name" },
        { label: "Booking Date", key: "booking_date", sortKey: "booking_date" },
        { label: "Source", key: "source", sortKey: "source" },
        { label: "Reservation Number", key: "reservation_number", sortKey: "reservation_number" },
        { label: "Guest Name", key: "guest_name", sortKey: "guest_name" },
        { label: "Check-in", key: "checkin_date", sortKey: "checkin_date" },
        { label: "Check-out", key: "checkout_date", sortKey: "checkout_date" },
        { label: "Extra (IDR)", key: "extra", sortKey: "extra" },
        { label: "Amount (IDR)", key: "amount", sortKey: "amount" },
    ];

    // Fungsi untuk mendapatkan ikon sorting
    const getSortIcon = (columnKey: OrderByOption) => {
        if (sortConfig.orderBy !== columnKey) {
            return null;
        }
        return sortConfig.sort === 'asc' ? <IconArrowUp size={14} /> : <IconArrowDown size={14} />;
    };

    const exportToPDF = () => {
        const doc = new jsPDF();
        doc.text("Sales Report V4", 14, 10);

        const tableData = data.data.map(row => columnsSales.map(col => {
            const value = row[col.key as keyof SalesReportDataV4];
            if (col.key === 'extra' || col.key === 'amount') {
                return formatPrice(Number(value));
            }
            return value;
        }));

        autoTable(doc, {
            head: [columnsSales.map(col => col.label)],
            body: tableData,
        });

        doc.save("SalesReportV4.pdf");
    };

    const exportToExcel = () => {
        const wsData = [
            columnsSales.map(col => col.label),
            ...data.data.map(row => columnsSales.map(col => {
                const value = row[col.key as keyof SalesReportDataV4];
                if (col.key === 'extra' || col.key === 'amount') {
                    return formatPrice(Number(value));
                }
                return value;
            })),
            // Baris total diperbarui untuk mencerminkan objek total V4
            ["Total", "", "", "", "", "", "", formatPrice(data.total.extra), formatPrice(data.total.amount)]
        ];

        const ws = XLSX.utils.aoa_to_sheet(wsData);
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, "Sales Report V4");
        const excelBuffer = XLSX.write(wb, { bookType: "xlsx", type: "array" });
        const blob = new Blob([excelBuffer], { type: "application/octet-stream" });
        saveAs(blob, "SalesReportV4.xlsx");
    };

    return (
        <div className="mt-4">
            <div className="mb-3 d-flex gap-2">
                <Button variant="primary" onClick={exportToPDF}>Export to PDF</Button>
                <Button variant="success" onClick={exportToExcel}>Export to Excel</Button>
            </div>

            <div className="table-responsive">
                <Table striped bordered hover>
                    <thead>
                        <tr>
                            {columnsSales.map((col) => (
                                <th
                                    key={col.key}
                                    onClick={() => onSort(col.sortKey)}
                                    style={{ cursor: 'pointer' }}
                                >
                                    <div className="d-flex align-items-center justify-content-between">
                                        {col.label}
                                        {getSortIcon(col.sortKey as OrderByOption)}
                                    </div>
                                </th>
                            ))}
                        </tr>
                    </thead>
                    <tbody>
                        {data?.data?.length > 0 ? (
                            data.data.map((row: SalesReportDataV4, rowIndex) => (
                                <tr key={rowIndex}>
                                    {columnsSales.map((col, colIndex) => (
                                        <td key={colIndex}>
                                            {
                                                (col.key === 'extra' || col.key === 'amount')
                                                    ? formatPrice(Number(row[col.key as keyof SalesReportDataV4]))
                                                    : row[col.key as keyof SalesReportDataV4]
                                            }
                                        </td>
                                    ))}
                                </tr>
                            ))
                        ) : (
                            <tr>
                                <td colSpan={columnsSales.length} className="text-center">
                                    No data available
                                </td>
                            </tr>
                        )}
                    </tbody>
                    <tfoot>
                        <tr>
                            <td colSpan={7}><strong>Total</strong></td>
                            <td><strong>{formatPrice(data.total.extra)}</strong></td>
                            <td><strong>{formatPrice(data.total.amount)}</strong></td>
                        </tr>
                    </tfoot>
                </Table>
            </div>
        </div>
    );
};

export default SalesReportTableV4;